import { Link } from "react-router-dom";
import { FadeUp, Eyebrow } from "../components/Reveal";

const POLICIES = [
  {
    id: "booking",
    title: "Booking & Confirmation",
    body: [
      "Every request is reviewed personally by Empress. A session is not confirmed until you receive an approval text or portal notification.",
      "Session times are held in Central Time. Please arrive prepared with lyrics, references and any files you'd like to work from.",
      "Sessions start at the booked time. Late arrivals still end at the scheduled time unless the calendar allows an extension.",
    ],
  },
  {
    id: "deals",
    title: "Deals & Packages",
    body: [
      "Individual rates: recording is $50 per hour and mixing is $75 per mix.",
      "2-Hour Special — $200 for two hours of recording with mix and master included.",
      "Monthly Deal — $500 for a month of recording, up to 3 hours a day, with all mixes included.",
      "Anything over the 3-hour threshold within the same day is charged at the hourly recording rate.",
      "Project Packages (lock-in): EP Package, 5–7 songs, $700. Album Package, 8+ songs, $1100. Both include recording and mixing for every song on the project.",
      "Deal and package rates are for Da Cave Studios, Nashville; Atlanta sessions carry the location adjustment shown on the Services page.",
    ],
  },
  {
    id: "deposits",
    title: "Deposits & Payment",
    body: [
      "Once approved, a deposit is encouraged to lock your time — it confirms your session instantly. The deposit percentage is shown on each service; packages carry a higher deposit so the project calendar stays yours.",
      "Deposits are paid securely by card through Stripe from your client portal. The remaining balance is due at your session.",
      "Prefer cash? Tick the cash option when booking (or in your portal after approval) — but only if you've arranged it with Empress. Your time is not locked until she confirms the cash was received.",
      "If a deposit is still open 48 hours after approval you'll receive a friendly reminder text; unpaid sessions may then be released back to the calendar.",
    ],
  },
  {
    id: "cancellation",
    title: "Rescheduling & Cancellation",
    body: [
      "Need to move your session? Message Empress through the portal or reply to your confirmation. Reschedules with at least 48 hours' notice carry your deposit forward at no charge.",
      "Cancellations or reschedules inside 48 hours forfeit the deposit. No-shows are charged the full session rate.",
      "If Empress must reschedule for any reason, your deposit carries forward in full or is refunded — your choice.",
    ],
  },
  {
    id: "files",
    title: "Files, Revisions & Delivery",
    body: [
      "Mixes and masters are delivered to your client portal, organized by project and version, where you can listen before downloading.",
      "Mixing includes two rounds of revisions. Additional revisions or re-cuts after approval are billed at the hourly rate.",
      "Please keep your own backups. Project files are stored for 90 days after final delivery.",
    ],
  },
  {
    id: "privacy",
    title: "Privacy & Communication",
    body: [
      "Your contact details are used only to manage your bookings, deliver your music and send session updates. They are never sold or shared.",
      "By providing a mobile number you agree to receive booking-related texts from Mixed By Empress. Reply STOP at any time to opt out; you'll still see every update in your portal.",
      "Your unreleased music is treated as confidential. Nothing is shared or featured publicly without your written permission.",
    ],
  },
];

export default function Policies() {
  return (
    <div className="bg-background" data-testid="policies-page">
      <section className="pt-36 pb-16 max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <FadeUp>
          <Eyebrow>Studio Policies</Eyebrow>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-obsidian">The <span className="italic text-purple-700">fine print</span>, kept clear.</h1>
          <p className="text-slate-500 mt-6 max-w-xl text-base">Everything you need to know about booking, deposits, files and how your information is handled. Questions? Reach Empress any time in your portal or at <a href="mailto:MixedByEmpress@gmail.com" className="text-purple-700 underline-offset-4 hover:underline">MixedByEmpress@gmail.com</a>.</p>
          <nav className="mt-8 flex flex-wrap gap-2" data-testid="policies-nav">
            {POLICIES.map((p) => (
              <a key={p.id} href={`#${p.id}`} className="px-4 py-2 rounded-full bg-white border border-purple-100 text-xs text-obsidian hover:border-gold transition-colors">{p.title}</a>
            ))}
          </nav>
        </FadeUp>
      </section>
      <section className="pb-24 lg:pb-32 max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid gap-6">
        {POLICIES.map((p, i) => (
          <FadeUp key={p.id} delay={i * 0.05}>
            <article id={p.id} className="bg-white rounded-3xl border border-purple-100 p-8 sm:p-10 grid gap-6 lg:grid-cols-12 scroll-mt-28" data-testid={`policy-${p.id}`}>
              <div className="lg:col-span-4">
                <p className="font-mono text-xs text-gold">0{i + 1}</p>
                <h2 className="font-serif text-2xl sm:text-3xl text-obsidian mt-2">{p.title}</h2>
              </div>
              <ul className="lg:col-span-8 space-y-4">
                {p.body.map((line, j) => (
                  <li key={j} className="flex gap-4 text-sm sm:text-base text-slate-600 leading-relaxed">
                    <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-purple-400 flex-shrink-0" />{line}
                  </li>
                ))}
              </ul>
            </article>
          </FadeUp>
        ))}
        <FadeUp className="text-center pt-8">
          <Link to="/book" data-testid="policies-book-btn" className="inline-block px-8 py-4 rounded-full bg-obsidian text-white text-sm hover:text-gold transition-colors">Ready? Book a session</Link>
        </FadeUp>
      </section>
    </div>
  );
}
